import { Box, Button, IconButton, makeStyles, Typography } from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ModalDialog from "../../../components/ModalDialog";
import AddSignature from "../../../components/icons/AddSignature";
import InfoDivider from "../../../components/InfoDivider";
import { useDataMutation } from "../../../hooks/useDataModule";
import { BlobContainerType } from "../../../models/generated/graphql";
import { UPDATE_PROFILE_PICTURE_OR_SIGNATURE } from "../queries";
import SignatureModal from "./SignatureModal";
import { useSetUserFromUserDataProps } from "../../../store/user";
import usePersonalInfo from "./use-personal-info";
import { LyfeTooltip } from "@lyfegen-platform/react-common";

const Signature = (props: { personalInfo: { userSignature: string | null; id: string; isSignatureCreationDraw: boolean | null } }) => {
	const [showSignatureModal, setShowSignatureModal] = useState<boolean>(false);
	const [isDeletionAlertOpen, setIsDeletionAlertOpen] = useState<boolean>(false);
	const [isUserUploadSignaturePresent, setIsUserUploadSignaturePresent] = useState<boolean>(
		!!props.personalInfo.userSignature && !props.personalInfo.isSignatureCreationDraw
	);
	const [isUserDrawSignaturePresent, setIsUserDrawSignaturePresent] = useState<boolean>(
		!!props.personalInfo.userSignature && !!props.personalInfo.isSignatureCreationDraw
	);
	const setUser = useSetUserFromUserDataProps();
	const [personalInfo] = usePersonalInfo();
	const classes = useStyles();
	const { t } = useTranslation();
	const [handleDelete] = useDataMutation(UPDATE_PROFILE_PICTURE_OR_SIGNATURE);

	const onDelete = () => {
		handleDelete({
			variables: {
				userProfileOrSignatureInput: {
					blobContainerType: BlobContainerType.Signature,
					blobName: `${props.personalInfo.id}${personalInfo.isSignatureCreationDraw ? "-signature-draw" : "-signature"}`,
					content: null,
					blobNameSource: `${props.personalInfo.id}${
						personalInfo.isSignatureCreationDraw ? "-signature-source-draw" : "-signature-source"
					}`,
					contentSource: null,
				},
			},
		});

		setIsUserDrawSignaturePresent(false);
		setIsUserUploadSignaturePresent(false);
		setIsDeletionAlertOpen(false);
		setUser({ ...personalInfo, sourceUserSignature: undefined, userSignature: undefined });
	};

	const deletionAlertText = {
		question: t("delete-signature-question"),
		reason: t("delete-signature-description"),
		continue: t("delete-continue"),
		discard: t("delete-discard"),
	};

	return (
		<>
			<InfoDivider paddingX={5} paddingTop={3} title="personal-signature" />
			<Box display="flex" flexDirection="row" width="100%" marginLeft={5} marginTop={2}>
				{(isUserUploadSignaturePresent || isUserDrawSignaturePresent) && personalInfo.sourceUserSignature ? (
					<Box position="relative">
						<img width="280px" height="120px" src={props.personalInfo.userSignature || ""} />
						<Box position="absolute" right="2%" top="2%">
							<LyfeTooltip title={t("edit-signature-button")}>
								<IconButton onClick={() => setShowSignatureModal(true)} color="primary">
									<Edit style={{ width: "18px", height: "18px" }} />
								</IconButton>
							</LyfeTooltip>
							<LyfeTooltip title={t("delete-signature-button")}>
								<IconButton onClick={() => setIsDeletionAlertOpen(true)} color="primary">
									<Delete style={{ width: "18px", height: "18px" }} />
								</IconButton>
							</LyfeTooltip>
						</Box>
					</Box>
				) : (
					<Button fullWidth classes={{ root: classes.signatureButton }} onClick={() => setShowSignatureModal(true)}>
						<Box>
							<Typography color="primary" style={{ marginBottom: 5 }}>
								{t("add-signature")}
							</Typography>
							<AddSignature />
						</Box>
					</Button>
				)}
				<SignatureModal
					isOpen={showSignatureModal}
					closeModal={() => setShowSignatureModal(false)}
					isUploadedImagePresent={isUserUploadSignaturePresent}
					isUserDrawSignaturePresent={isUserDrawSignaturePresent}
					setIsUploadedImagePresent={setIsUserUploadSignaturePresent}
					setIsUserDrawSignaturePresent={setIsUserDrawSignaturePresent}
				/>
				<ModalDialog
					isOpen={isDeletionAlertOpen}
					text={deletionAlertText}
					onContinueClick={onDelete}
					onDiscardClick={() => setIsDeletionAlertOpen(false)}
				/>
			</Box>
		</>
	);
};

export default Signature;

const useStyles = makeStyles({
	signatureButton: {
		width: "276px",
		height: "120px",
		backgroundColor: "#f5f8f9",
		textTransform: "none",
		"&:hover": {
			boxShadow: "none",
		},
	},
});
